import React, { useEffect, useState, useCallback } from 'react';
import {
  Loader2, RefreshCw, Workflow, Phone, Mail, Flame, MoreHorizontal, X,
  ArrowRight, CheckCircle2, Tag as TagIcon, AlertTriangle,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * PodPipelineBoard — 4-stage active pipeline.
 *   New Lead → Chasing → Nurturing → Ready to Proceed
 * Closed outcomes (not_suitable / went_elsewhere / no_response / spam) live
 * outside the board. "Ready to Proceed" cards expose a "Push to Bank CRM"
 * action.
 */

const OWNER_META = {
  csm:    { label: 'CSM',    cls: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300' },
  broker: { label: 'Broker', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300' },
};

const TEMP_META = {
  hot:  { cls: 'bg-rose-100 text-rose-700 border-rose-200', label: 'Hot' },
  warm: { cls: 'bg-amber-100 text-amber-700 border-amber-200', label: 'Warm' },
  cold: { cls: 'bg-blue-100 text-blue-700 border-blue-200', label: 'Cold' },
};

const COLOR_MAP = {
  blue:    { ring: 'border-t-blue-500',    bg: 'bg-blue-50/40 dark:bg-blue-950/20',     dot: 'bg-blue-500' },
  amber:   { ring: 'border-t-amber-500',   bg: 'bg-amber-50/40 dark:bg-amber-950/20',   dot: 'bg-amber-500' },
  violet:  { ring: 'border-t-violet-500',  bg: 'bg-violet-50/40 dark:bg-violet-950/20', dot: 'bg-violet-500' },
  emerald: { ring: 'border-t-emerald-500', bg: 'bg-emerald-50/40 dark:bg-emerald-950/20', dot: 'bg-emerald-500' },
  slate:   { ring: 'border-t-slate-400',   bg: 'bg-slate-50/40 dark:bg-slate-900/20',   dot: 'bg-slate-400' },
};

// ───────────── Lead Card ─────────────
const LeadCard = ({ lead, onOpen, onDragStart, onDragEnd, dragging, onPushToBank, onClose, onActions }) => {
  const temp = (lead.temperature || lead.priority || '').toLowerCase();
  const tempMeta = TEMP_META[temp];
  const tags = lead.tags || [];
  const isReady = lead.pod_stage === 'ready_to_proceed';
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, lead)}
      onDragEnd={onDragEnd}
      className={cn(
        'group cursor-grab active:cursor-grabbing bg-white dark:bg-slate-900/60 border border-surface-border dark:border-slate-800 rounded-lg p-3 hover:border-crm-blue/40 hover:shadow-sm transition-all',
        dragging && 'opacity-50',
      )}
      data-testid={`pod-card-${lead.id}`}
    >
      <button type="button" onClick={() => onOpen && onOpen(lead)} className="w-full text-left">
        <div className="flex items-start justify-between gap-2 mb-1">
          <span className="font-bold text-sm text-slate-900 dark:text-white truncate">{lead.name}</span>
          {tempMeta && (
            <span className={cn('text-[9px] font-bold px-1.5 py-0.5 rounded border whitespace-nowrap', tempMeta.cls)}>
              {tempMeta.label}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400 truncate">
          {lead.phone && <span className="inline-flex items-center gap-1"><Phone className="w-2.5 h-2.5" />{lead.phone}</span>}
          {!lead.phone && lead.email && <span className="inline-flex items-center gap-1"><Mail className="w-2.5 h-2.5" />{lead.email}</span>}
        </div>
        {(lead.is_speed_urgent || lead.next_action_overdue || lead.portal_stale) && (
          <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-bold text-rose-600 dark:text-rose-400">
            <Flame className="w-2.5 h-2.5" />
            {lead.is_speed_urgent
              ? `${lead.minutes_since_received}m to call`
              : lead.portal_stale
                ? `Portal ${Math.round(lead.portal_stale_hours)}h stale`
                : 'Follow-up overdue'}
          </div>
        )}
        {tags.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {tags.slice(0, 3).map((t) => (
              <span key={t} className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 font-semibold">
                <TagIcon className="w-2 h-2" />{t}
              </span>
            ))}
            {tags.length > 3 && <span className="text-[9px] text-slate-500 dark:text-slate-400 font-bold">+{tags.length - 3}</span>}
          </div>
        )}
        {lead.next_action && (
          <div className="mt-1.5 text-[11px] text-slate-700 dark:text-slate-200 truncate">
            → {lead.next_action}
          </div>
        )}
      </button>
      {/* Quick actions row */}
      <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-800 flex items-center gap-1.5">
        {isReady ? (
          <button
            type="button"
            onClick={() => onPushToBank && onPushToBank(lead)}
            className="flex-1 text-[10px] font-bold px-2 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center justify-center gap-1 transition-colors"
            data-testid={`push-bank-${lead.id}`}
          >
            <ArrowRight className="w-3 h-3" /> Push to Bank CRM
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onOpen && onOpen(lead)}
            className="flex-1 text-[10px] font-bold px-2 py-1.5 rounded-md bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 transition-colors"
          >
            Open
          </button>
        )}
        <button
          type="button"
          onClick={() => onClose && onClose(lead)}
          className="text-[10px] font-bold px-2 py-1.5 rounded-md text-slate-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 transition-colors"
          title="Close out with an outcome"
          data-testid={`close-${lead.id}`}
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

// ───────────── Outcome Dialog ─────────────
const OutcomeDialog = ({ open, onOpenChange, lead, outcomes, onConfirmed }) => {
  const [outcome, setOutcome] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setOutcome(''); setReason(''); } }, [open]);
  const handleConfirm = async () => {
    if (!outcome || !lead) return;
    setSaving(true);
    try {
      await onConfirmed(lead, { outcome, reason });
      onOpenChange(false);
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="outcome-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Close lead{lead ? `: ${lead.name}` : ''}
          </DialogTitle>
          <DialogDescription>Pick an outcome — the lead will leave the active pipeline.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-2">
            {(outcomes || []).map((o) => (
              <button
                key={o.key}
                type="button"
                onClick={() => setOutcome(o.key)}
                className={cn(
                  'px-3 py-2.5 rounded-lg text-sm font-bold border-2 transition-all text-left',
                  outcome === o.key
                    ? 'border-rose-500 bg-rose-50 dark:bg-rose-950/30 text-rose-900 dark:text-rose-200'
                    : 'border-slate-200 dark:border-slate-700 hover:border-rose-300 text-slate-700 dark:text-slate-300',
                )}
                data-testid={`outcome-option-${o.key}`}
              >
                {o.label}
              </button>
            ))}
          </div>
          <div>
            <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-1 block">
              Reason (optional)
            </Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. went with bank direct" data-testid="outcome-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button
            onClick={handleConfirm}
            disabled={saving || !outcome}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="outcome-confirm"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Close lead'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ───────────── Push to Bank CRM Dialog ─────────────
const PushBankDialog = ({ open, onOpenChange, lead, onConfirmed }) => {
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setNotes(''); }, [open]);
  const handleConfirm = async () => {
    if (!lead) return;
    setSaving(true);
    try {
      await onConfirmed(lead, { notes });
      onOpenChange(false);
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="push-bank-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            Push to Bank CRM
          </DialogTitle>
          <DialogDescription>
            Move <strong>{lead?.name}</strong> into the home loan CRM. The lead will leave Lead Nurture.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-1 block">
            Handoff notes (optional)
          </Label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. ready to submit — refinance, $750k" data-testid="push-notes" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleConfirm} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="push-confirm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Push to Bank CRM'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ───────────── Main Board ─────────────
const PodPipelineBoard = ({ token, onOpenLead }) => {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dragLead, setDragLead] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [outcomeLead, setOutcomeLead] = useState(null);
  const [pushLead, setPushLead] = useState(null);

  const fetchBoard = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await fetch(`${backendUrl}/api/nurture/board`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      setBoard(d || null);
    } catch (e) {
      setBoard(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchBoard(); }, [fetchBoard]);

  const handleDrop = async (e, targetStage) => {
    e.preventDefault();
    setDragOverCol(null);
    if (!dragLead || dragLead.pod_stage === targetStage) return;
    const lead = dragLead;
    setDragLead(null);
    setBoard((prev) => {
      if (!prev) return prev;
      const cols = prev.columns.map((c) => {
        if (c.key === lead.pod_stage) return { ...c, leads: c.leads.filter((l) => l.id !== lead.id), count: c.count - 1 };
        if (c.key === targetStage) return { ...c, leads: [{ ...lead, pod_stage: targetStage }, ...c.leads], count: c.count + 1 };
        return c;
      });
      return { ...prev, columns: cols };
    });
    try {
      const res = await fetch(`${backendUrl}/api/nurture/leads/${lead.id}/stage`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ pod_stage: targetStage }),
      });
      if (!res.ok) throw new Error('move failed');
      toast.success(`Moved ${lead.name}`);
    } catch (e) {
      toast.error('Could not move lead — refreshing');
      fetchBoard();
    }
  };

  const handleConfirmOutcome = async (lead, { outcome, reason }) => {
    try {
      const res = await fetch(`${backendUrl}/api/nurture/leads/${lead.id}/outcome`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ outcome, reason }),
      });
      if (!res.ok) throw new Error('outcome save failed');
      toast.success(`${lead.name} closed`);
      fetchBoard();
    } catch (e) {
      toast.error('Could not save outcome');
    }
  };

  const handleConfirmPush = async (lead, { notes }) => {
    try {
      const res = await fetch(`${backendUrl}/api/nurture/leads/${lead.id}/push-to-bank-crm`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes }),
      });
      if (!res.ok) throw new Error('push failed');
      toast.success(`${lead.name} pushed to Bank CRM`);
      fetchBoard();
    } catch (e) {
      toast.error('Could not push to Bank CRM');
    }
  };

  if (loading || !board) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="pod-board-loading">
        <Loader2 className="w-6 h-6 animate-spin text-crm-blue" />
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="pod-pipeline-board">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight flex items-center gap-2">
            <Workflow className="w-5 h-5 text-crm-blue" />
            Lead Pipeline
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            4 active stages · Closed outcomes &amp; tags handled separately
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchBoard} title="Refresh">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {board.columns.map((col) => {
          const colorMeta = COLOR_MAP[col.color] || COLOR_MAP.slate;
          const ownerMeta = OWNER_META[col.owner] || OWNER_META.csm;
          return (
            <div
              key={col.key}
              onDragOver={(e) => { e.preventDefault(); setDragOverCol(col.key); }}
              onDragLeave={() => setDragOverCol(null)}
              onDrop={(e) => handleDrop(e, col.key)}
              className={cn(
                'rounded-xl border border-surface-border dark:border-slate-800 border-t-4',
                colorMeta.ring, colorMeta.bg,
                dragOverCol === col.key && 'ring-2 ring-crm-blue',
              )}
              data-testid={`pod-col-${col.key}`}
            >
              <div className="px-3 py-2.5 flex items-center gap-2 border-b border-surface-border/60 dark:border-slate-800">
                <span className={cn('w-2 h-2 rounded-full flex-shrink-0', colorMeta.dot)} />
                <h3 className="text-xs font-extrabold uppercase tracking-wide text-slate-900 dark:text-white truncate">
                  {col.label}
                </h3>
                <span className="ml-auto inline-flex items-center px-1.5 py-0.5 rounded-full bg-white dark:bg-slate-900/60 border border-surface-border dark:border-slate-700 text-[10px] font-bold tabular-nums">
                  {col.count}
                </span>
                <span className={cn('text-[9px] font-bold px-1.5 py-0.5 rounded', ownerMeta.cls)}>
                  {ownerMeta.label}
                </span>
              </div>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 px-3 pt-2 leading-snug">{col.description}</p>
              <div className="p-2 space-y-2 min-h-[200px] max-h-[640px] overflow-y-auto">
                {col.leads.length === 0 ? (
                  <div className="text-center text-[11px] text-slate-400 dark:text-slate-500 py-6 italic">
                    Drop leads here
                  </div>
                ) : (
                  col.leads.map((lead) => (
                    <LeadCard
                      key={lead.id}
                      lead={lead}
                      onOpen={onOpenLead}
                      onDragStart={(e, l) => { setDragLead(l); e.dataTransfer.effectAllowed = 'move'; }}
                      onDragEnd={() => { setDragLead(null); setDragOverCol(null); }}
                      dragging={dragLead?.id === lead.id}
                      onPushToBank={(l) => setPushLead(l)}
                      onClose={(l) => setOutcomeLead(l)}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      <OutcomeDialog
        open={!!outcomeLead}
        onOpenChange={(o) => { if (!o) setOutcomeLead(null); }}
        lead={outcomeLead}
        outcomes={board.outcomes || []}
        onConfirmed={handleConfirmOutcome}
      />
      <PushBankDialog
        open={!!pushLead}
        onOpenChange={(o) => { if (!o) setPushLead(null); }}
        lead={pushLead}
        onConfirmed={handleConfirmPush}
      />
    </div>
  );
};

export default PodPipelineBoard;
